"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import DOMPurify from "isomorphic-dompurify";
import { usePathname } from "next/navigation";
import Script from "next/script";

import { useConfig } from "@jti/app-context/useConfig";
import { GTMEVARunner } from "@jti/tracker";

import { useGTMInitState } from "@/context/GTMContext";
import { type PageConfig, usePageConfig } from "@/context/PageConfigContext";

const sanitize = DOMPurify.sanitize;

type GTMRunnerProps = {
  pageConfigOverride?: Pick<PageConfig, "analytics" | "pageName" | "pageLoadTracking">;
};

export const GTMRunner = ({ pageConfigOverride }: GTMRunnerProps) => {
  const pageConfig = usePageConfig();
  const [pageValues, setPageValues] = useState(pageConfig);
  const { analytics, pageName, pageLoadTracking } = pageValues;
  const pathname = usePathname();
  const gtmContainerRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const { GTM_CODE, GTM_DOMAIN } = useConfig();
  const { setIsGTMInitialized } = useGTMInitState();

  const GTMDomain = GTM_DOMAIN || "https://www.googletagmanager.com";

  useEffect(() => {
    if (pageConfigOverride) {
      setPageValues((prev) => ({
        ...prev,
        ...pageConfigOverride,
      }));
    }

    setMounted(true);
  }, [pathname, pageConfigOverride]);

  useEffect(() => {
    if (!mounted || !analytics) return;

    let cancelled = false;

    const GTMRunner = GTMEVARunner.getInstance(gtmContainerRef.current, pageName || "other");

    const initGTM = async () => {
      try {
        await GTMRunner.waitForInstance(GTMRunner.promiseTimeoutInMs);
        if (cancelled) return;
        setIsGTMInitialized(true);
      } catch (err) {
        if (!cancelled) {
          console.error("GTM failed to initialize", err);
        }
      }
    };

    initGTM();

    return () => {
      cancelled = true;
    };
  }, [analytics, mounted, pageName, setIsGTMInitialized]);

  return (
    <>
      {mounted &&
        analytics &&
        createPortal(
          <div
            id="gtmContainer"
            data-page-name={pageName}
            data-gtm-code={GTM_CODE}
            data-on-load-tracking={pageLoadTracking}
            ref={gtmContainerRef}
          />,
          // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
          document.body
        )}
      <div hidden>
        <Script
          id="gtm-script"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: sanitize(`(function (w, d, s, l, i) {
              w[l] = w[l] || [];
              w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
              });
              var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
              j.async = true;
              j.src = '${GTMDomain}/gtm.js?id=' + i + dl;
              f.parentNode.insertBefore(j, f);
            })(window, document, 'script', 'dataLayer', "${GTM_CODE}");`),
          }}
        />
      </div>
    </>
  );
};
